import { Injectable } from '@angular/core';
import { Nullable } from 'primeng/ts-helpers';
import { BehaviorSubject, Observable } from 'rxjs';

import { AppService } from '@/service/app.service';

@Injectable({
    providedIn: 'root'
})
export class ValidationService {
    constructor(private appService: AppService) {}

    private _errors$: Record<string, BehaviorSubject<Nullable<string>>> = {};

    setError(group: string, field: string, error: string): void {
        if (!this._errors$[`${group}.${field}`]) {
            this._errors$[`${group}.${field}`] = new BehaviorSubject<Nullable<string>>(error);
            return;
        }
        this._errors$[`${group}.${field}`].next(error);
    }

    setErrors(group: string, errors: Record<string, string>): void {
        Object.entries(errors).forEach(([field, error]) => {
            if (!this._errors$[`${group}.${field}`]) {
                this._errors$[`${group}.${field}`] = new BehaviorSubject<Nullable<string>>(error);
                return;
            }
            this._errors$[`${group}.${field}`].next(error);
        });
    }

    getError(group: string, field: string): Nullable<string> {
        return this._errors$[`${group}.${field}`]?.getValue() ?? null;
    }

    getErrors(group: Nullable<string> = null): Record<string, Nullable<string>> {
        const errors: Record<string, string> = {};
        Object.entries(this._errors$).forEach(([key, subject]) => {
            const [errGroup, errField] = key.split('.');
            if (group === null || group === errGroup) {
                const error = subject.getValue();
                if (error) {
                    errors[errField] = error;
                }
            }
        });
        return errors;
    }

    getError$(group: string, field: string): Observable<Nullable<string>> {
        if (!this._errors$[`${group}.${field}`]) {
            this._errors$[`${group}.${field}`] = new BehaviorSubject<Nullable<string>>(null);
        }
        return this._errors$[`${group}.${field}`].asObservable();
    }

    clearError(group: string, field: string): void {
        if (this._errors$[`${group}.${field}`]) {
            this._errors$[`${group}.${field}`].next(null);
        }
    }

    clearErrors(group: string): void {
        Object.keys(this._errors$).forEach((key) => {
            const [errGroup, _] = key.split('.');
            if (group === errGroup) {
                this._errors$[key].next(null);
            }
        });
    }

    clearAllErrors(): void {
        Object.values(this._errors$).forEach((subject) => {
            subject.next(null);
        });
    }

    hasErrors(group: Nullable<string> = null): boolean {
        if (group) {
            return Object.keys(this._errors$).some((key) => {
                const [errGroup, _] = key.split('.');
                const error = this._errors$[key].getValue();
                return errGroup === group && !!error;
            });
        }
        return Object.values(this._errors$).some((subject) => !!subject.getValue());
    }

    hasError(group: string, field: string): boolean {
        return !!this._errors$[`${group}.${field}`]?.getValue();
    }

    checkValidity(group: Nullable<string> = null): boolean {
        if (this.hasErrors(group)) {
            let message = 'Please review and correct the following errors:<br><br>';
            const errors = this.getErrors(group);
            if (Object.keys(errors).length) {
                Object.entries(errors).forEach(([, error]) => {
                    message += `• ${error}<br>`;
                });
            } else {
                message += '• No errors found. Please review the form and try again.';
            }

            this.appService.toast(message, 'warn');

            return false;
        }

        return true;
    }

    isValidUrl(value: string): boolean {
        try {
            new URL(value);
            return true;
        } catch (_) {
            return false;
        }
    }
}
